import React, { useState, useEffect } from 'react';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import Search from 'ps-components/Search/Search';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import { array, bool, func, number, shape, string } from 'prop-types';
import sortBy from 'lodash/sortBy';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import cn from 'classnames';

import './ViewList.scss';

const scopeMapping = {
  personal: 'Personal',
  group: 'Group',
  site: 'Site',
  global: 'Global'
};

const columnHeaders = [
  {
    Header: 'View Name',
    width: 420,
    accessor: 'view_name'
  },
  {
    Header: 'View Type',
    width: 150,
    accessor: 'scope'
  },
  {
    Header: 'Last modified',
    width: 150,
    accessor: 'last_modified_time'
  },
  {
    Header: 'Created by',
    width: 260,
    accessor: 'creator_name'
  }
];

ViewList.propTypes = {
  views: array,
  defaultViewId: number,
  currentViewId: number,
  toggleEditView: func,
  setEditMode: func,
  setSelectedView: func,
  onRefresh: func,
  pageCode: string,
  maid: string,
  pageName: string,
  permissions: shape({
    regularUser: bool,
    readOnlyUser: bool,
    reportViewUser: bool,
    restrictedUser: bool,
    groupAdmin: bool,
    siteAdmin: bool,
    globalAdmin: bool,
    superAdmin: bool
  }),
  subsite: string,
  marketPricing: shape({
    deleteView: shape({
      loading: bool,
      error: string
    }),
    moveView: shape({
      loading: bool,
      error: string
    }),
    setDefault: shape({
      loading: bool,
      error: string
    })
  }),
  actions: shape({
    showSlideout: func,
    hideSlideout: func,
    onDelete: func,
    onMove: func,
    setAsDefault: func
  }),
  groupId: number,
  organizationId: number,
  regularUserCanConfigureViews: bool,
  groupAdminCanConfigureViews: bool,
  onCreate: func,
  onDelete: func,
  onCopy: func,
  onClickView: func
};

function ViewList(props) {
  const [searchText, setSearchText] = useState('');
  const [showEditDropdown, setShowEditDropdown] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [filteredViews, setFilteredViews] = useState(getFilteredViews());

  const isReadOnly = props.permissions?.readOnlyUser;
  const isRestricted = props.permissions?.restrictedUser;

  if (isRestricted) return null;

  useEffect(() => {
    setFilteredViews(getFilteredViews());

    // weird bug with views not appearing on refresh
    if (props.views.length === 0) {
      setSearchText('');
    }
  }, [props.views]);

  useEffect(() => {
    setFilteredViews(getFilteredViews());
  }, [searchText]);

  function getFilteredViews() {
    const scopeOrder = ['personal', 'group', 'site', 'global'];
    let orderedList = [];

    let searchedViews = searchText
      ? props.views?.filter(view => view?.view_name.match(new RegExp(searchText, 'i')))
      : props.views;

    scopeOrder.forEach(s => {
      const scopeViews = [];
      searchedViews.forEach(v => {
        if (v.scope === s) {
          v.view_name_lower = v.view_name?.toLowerCase();
          scopeViews.push(v);
        }
      });

      const sorted = sortBy(scopeViews, ['view_name_lower'], ['asc']);
      orderedList = orderedList.concat(sorted);
    });

    return orderedList;
  }

  const isLoading =
    props.marketPricing?.deleteView?.loading ||
    props.marketPricing?.moveView?.loading ||
    props.marketPricing?.setDefault?.loading;
  const isGroupSameAsOrg = props.groupId === props.organizationId;

  function searchOnChange(event) {
    setSearchText(event?.target?.value);
  }

  function searchOnClick() {
    setSearchText('');
  }

  function searchOnKeyDown(event) {
    if (event.key === 'Enter') setSearchText(searchText);
  }

  function searchOnSearch() {
    setSearchText(searchText);
  }

  function handleEditView(row) {
    setSelectedRow(row);
    setShowEditDropdown(!showEditDropdown);
    setShowDeleteButton(false);
  }

  function resetEdit() {
    setSelectedRow({});
    setShowDeleteButton(false);
    setShowEditDropdown(false);
  }

  function handleDeleteView() {
    const { maid } = props;
    const deleteAction = props.onDelete ? props.onDelete : props.actions.onDelete;
    deleteAction(selectedRow.view_pk, selectedRow.old_view_pk, maid).then(() => {
      props.onRefresh();
      setShowEditDropdown(false);
      setShowDeleteButton(false);
    });
  }

  function hasPermission(scope) {
    const {
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      groupAdmin,
      siteAdmin,
      globalAdmin,
      superAdmin
    } = props.permissions;

    const { subsite, groupAdminCanConfigureViews, regularUserCanConfigureViews } = props;

    // read-only users can edit and create their own personal views
    if (readOnlyUser) {
      return scope === 'personal';
    }

    if (reportViewUser || restrictedUser) return false;

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;
    const globalOrAbove = globalAdmin || superAdmin;

    let permission = false;

    if (scope === 'personal') {
      permission = true;
    } else if (scope === 'group' && groupOrAbove && !isGroupSameAsOrg) {
      permission = true;
    } else if (scope === 'site' && siteOrAbove) {
      permission = true;
    } else if (scope === 'global' && globalOrAbove && subsite === 'marketpaymaster') {
      permission = true;
    }

    if (!groupOrAbove && !regularUserCanConfigureViews) {
      permission = false;
    }

    if (!siteOrAbove && groupOrAbove && !groupAdminCanConfigureViews) {
      permission = false;
    }

    return permission;
  }

  function getCopyOption(view) {
    const {
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      groupAdmin,
      siteAdmin,
      globalAdmin,
      superAdmin
    } = props.permissions;

    const { groupAdminCanConfigureViews, regularUserCanConfigureViews } = props;

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;

    if (reportViewUser || restrictedUser) return null;

    if (!groupOrAbove && !regularUserCanConfigureViews) {
      return null;
    }

    if (!siteOrAbove && groupOrAbove && !groupAdminCanConfigureViews) {
      return null;
    }

    return (
      <a className="pxl-dropdown__item" onClick={() => onClickCopy(view)}>
        Copy this view
      </a>
    );
  }

  function onClickCopy(view) {
    const { setSelectedView, setEditMode, toggleEditView, onCopy } = props;
    if (onCopy) {
      onCopy(view).then(props.onRefresh);
      return;
    }
    // need to clone so the original view in view-list isn't affected by reference
    const copiedView = cloneDeep(view);

    // set scope to personal
    copiedView.scope = 'personal';
    // set flag for copy, so we can delete report_pk and view_pk before saving
    copiedView.isCopy = true;
    // set name to copy of original
    copiedView.view_name = `Copy of ${view.view_name}`;

    setSelectedView(copiedView);
    setEditMode(true);
    toggleEditView(true);
  }

  function getDeleteOption(view) {
    const canDelete = hasPermission(view.scope);

    if (canDelete) {
      return (
        <a className="pxl-dropdown__item" onClick={() => setShowDeleteButton(true)}>
          Delete this view
        </a>
      );
    }

    return null;
  }

  function setAsDefault(view) {
    const { maid, pageCode } = props;

    props.actions.setAsDefault(view.view_pk, pageCode, maid).then(() => {
      props.onRefresh();
      setShowEditDropdown(false);
    });
  }

  function getDefaultOption(view) {
    const canSetDefault = hasPermission(view.scope);

    if (canSetDefault) {
      return (
        <a className="pxl-dropdown__item" onClick={() => setAsDefault(view)}>
          Set as default
        </a>
      );
    }

    return null;
  }

  function handleOnMove(view, newScope) {
    const { maid } = props;

    props.actions.onMove(view.view_pk, newScope, maid).then(() => {
      props.onRefresh();
      setShowEditDropdown(false);
    });
  }

  function getMoveOption(name, newScope, view) {
    return (
      <a className="pxl-dropdown__item" onClick={() => handleOnMove(view, newScope)}>
        {name}
      </a>
    );
  }

  function canUserCreate() {
    const { reportViewUser, restrictedUser, groupAdmin, siteAdmin, globalAdmin, superAdmin } = props.permissions;
    const { groupAdminCanConfigureViews, regularUserCanConfigureViews } = props;

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;

    if (restrictedUser || reportViewUser) return false;

    // regular users
    if (!groupOrAbove) {
      if (regularUserCanConfigureViews) return true;
      else return false;
    }

    // group admins
    if (!siteOrAbove) {
      if (groupAdminCanConfigureViews) return true;
      else return false;
    }

    // site and above admins
    return true;
  }

  function getMoveOptions(view) {
    const {
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      groupAdmin,
      siteAdmin,
      globalAdmin,
      superAdmin
    } = props.permissions;

    const { subsite, groupAdminCanConfigureViews, regularUserCanConfigureViews } = props;
    const viewScope = view.scope;

    const nope = readOnlyUser || reportViewUser || restrictedUser;
    if (nope) return [];

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;
    const globalOrAbove = globalAdmin || superAdmin;

    let moveToOptions = [];

    // moving personal view to group
    if (groupOrAbove && !isGroupSameAsOrg && viewScope === 'personal') {
      moveToOptions.push(getMoveOption('Move to group view', 'group', view));
    }

    // moving personal/group view to site
    if (siteOrAbove && (viewScope === 'personal' || viewScope === 'group')) {
      moveToOptions.push(getMoveOption('Move to site view', 'site', view));
    }

    // moving site view to global
    if (
      globalOrAbove &&
      (viewScope === 'personal' || viewScope === 'group' || viewScope === 'site') &&
      subsite === 'marketpaymaster'
    ) {
      moveToOptions.push(getMoveOption('Move to global view', 'global', view));
    }

    // moving group or site view to personal
    if ((groupOrAbove && viewScope === 'group') || (siteOrAbove && viewScope === 'site')) {
      moveToOptions.push(getMoveOption('Move to personal view', 'personal', view));
    }

    // moving site to group
    if (siteOrAbove && !isGroupSameAsOrg && viewScope === 'site') {
      moveToOptions.push(getMoveOption('Move to group view', 'group', view));
    }

    // moving global view to personal
    if (globalOrAbove && viewScope === 'global' && subsite === 'marketpaymaster') {
      moveToOptions.push(getMoveOption('Move to personal view', 'personal', view));
    }

    if (!groupOrAbove && !regularUserCanConfigureViews) {
      moveToOptions = [];
    }

    if (!siteOrAbove && groupOrAbove && !groupAdminCanConfigureViews) {
      moveToOptions = [];
    }

    return moveToOptions;
  }

  function formatDate(date) {
    return moment(date).format('MMM Do, YYYY');
  }

  function onClickView(view, isDefaultView, isSelectedView) {
    if (props.onClickView) {
      props.onClickView(
        {
          group: hasPermission('group'),
          personal: hasPermission('personal'),
          site: hasPermission('site')
        },
        view,
        isDefaultView,
        isSelectedView
      );
      return;
    }
    const { setSelectedView, setEditMode, toggleEditView } = props;
    // need to clone so the original view in view-list isn't affected by reference
    const copiedView = cloneDeep(view);

    setSelectedView(copiedView);
    setEditMode(true);
    toggleEditView(true);
  }

  function getViewRow(row) {
    const { globalAdmin, superAdmin } = props.permissions;

    const isCurrentRow = row.original.view_pk === selectedRow.view_pk;
    const isSelectedDeleteRow = showDeleteButton && isCurrentRow;

    const isDefaultView = row.original?.view_pk === props.defaultViewId;
    const isSelectedView = row.original?.view_pk === props.currentViewId;
    const isBothDefaultAndSelected = isDefaultView && isSelectedView;

    const moveOptions = getMoveOptions(row.original);
    const deleteOption = getDeleteOption(row.original);
    const copyOption = getCopyOption(row.original);
    const defaultOption = isDefaultView ? null : getDefaultOption(row.original);

    const keyName = row?.original?.view_pk;

    const globalOrAbove = globalAdmin || superAdmin;
    const unclickable = row?.original?.scope === 'global' && !(globalOrAbove && props.subsite === 'marketpaymaster');

    let defaultTitle = null;
    if (isBothDefaultAndSelected) {
      defaultTitle = <div className="default-view-title">Default and Current</div>;
    } else if (isDefaultView) {
      defaultTitle = <div className="default-view-title">Default</div>;
    } else if (isSelectedView) {
      defaultTitle = <div className="default-view-title">Current</div>;
    }

    const dropdownOptions = [];
    if (moveOptions.length > 0) dropdownOptions.push(...moveOptions);
    if (deleteOption) dropdownOptions.push(deleteOption);
    if (copyOption) dropdownOptions.push(copyOption);
    if (defaultOption) dropdownOptions.push(defaultOption);

    const showMoreOptions = dropdownOptions.length > 0;

    const ellipsisElement = (
      <div onClick={() => handleEditView(row.original)} className="configure-view__vertical-ellipsis">
        <div className="circle-dot"></div>
        <div className="circle-dot"></div>
        <div className="circle-dot"></div>
      </div>
    );

    return (
      <div className="configure-view__table-row">
        <AdvancedCell getCellProps={row.cells[0].getCellProps}>
          {defaultTitle}
          <div
            className={cn('configure-view__name-cell', { unclickable })}
            onClick={unclickable ? null : () => onClickView(row?.original, isDefaultView, isSelectedView)}
            data-tip={row?.original?.view_description}
            data-row-key={`tooltip-${keyName}`}
            key={`tooltip-${keyName}`}
          >
            {row.original?.view_name}
          </div>
        </AdvancedCell>

        <AdvancedCell getCellProps={row.cells[1].getCellProps}>
          {isSelectedDeleteRow ? null : <div className="configure-view__cell">{scopeMapping[row.original?.scope]}</div>}
        </AdvancedCell>

        <AdvancedCell getCellProps={row.cells[2].getCellProps}>
          {isSelectedDeleteRow ? (
            <p>Delete this view?</p>
          ) : (
            <div className="configure-view__cell">{formatDate(row?.original?.last_modified_time?.split(' ')[0])}</div>
          )}
        </AdvancedCell>

        <AdvancedCell getCellProps={row.cells[3].getCellProps}>
          <div className="configure-view__cell configure-view__cell-edit">
            {isSelectedDeleteRow ? (
              <div className="configure-view__delete-wrapper">
                <a className="configure-view__cancel-link" onClick={resetEdit}>
                  Cancel
                </a>
                <button onClick={handleDeleteView} className="configure-view__delete-btn">
                  Delete
                </button>
              </div>
            ) : (
              <div>{row.original?.creator_name}</div>
            )}
            {!showDeleteButton && showMoreOptions && (
              <Dropdown customDropdownTrigger={ellipsisElement} dropdownItems={dropdownOptions} />
            )}
          </div>
        </AdvancedCell>

        <ReactTooltip place="bottom" />
      </div>
    );
  }

  function createNew() {
    if (props.onCreate) {
      props.onCreate({
        group: hasPermission('group'),
        personal: hasPermission('personal'),
        site: hasPermission('site')
      });
      return;
    }
    props.setEditMode(false);
    props.toggleEditView();
    props.setSelectedView(null);
  }

  return (
    <div className="configure-view__view-list">
      <SlideoutHeader
        title={`Edit ${props.pageName}`}
        headerClassName="configure-view__slideout-header"
        closeButtonClassName="configure-view__slideout-header-close"
        doClose={props?.navigateToPrevSlideout || props.hideSlideout}
        additionalButtons={
          !canUserCreate() ? null : (
            <SlideoutHeaderButton buttonClass="configure-view__header-button" onClick={createNew}>
              Create New
            </SlideoutHeaderButton>
          )
        }
      />

      {isLoading ? (
        <InPlaceIndicator />
      ) : (
        <div className="configure-view__content-wrapper">
          <div className="configure-view__search-wrapper">
            <Search
              className="configure-view__table__search"
              placeholder="Search View Names"
              onChange={searchOnChange}
              onClick={searchOnClick}
              onKeyDown={searchOnKeyDown}
              onSearch={searchOnSearch}
              value={searchText}
            />
          </div>
          <AdvancedTable
            isBasic={true}
            staticTable={false}
            columnHeaders={columnHeaders}
            rowData={filteredViews}
            getDataRow={getViewRow}
            allowSorting={true}
            useClientSideSort={true}
            className="configure-view__table"
            headerClassName="configure-view__table-header"
          />
        </div>
      )}
    </div>
  );
}

export default ViewList;
